// askSmart-ui/src/pages/TOSP/components/BmvaFeesPanel.tsx
import React, { useEffect, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import { formatCurrency } from "../format";
import { bmvaAnnualFee, isBmvaExempt, Tenant, Unit } from "../types";
import { parseNumeric } from "../validation";
// Where the shared "i" toggle is styled — imported so this panel doesn't rely
// on the Natural Breakpoint component happening to be in the same bundle.
import "./NaturalBreakpointDetail.css";

interface BmvaFeesPanelProps {
  /** $ per BMVA square foot per year. Null until an admin states it. */
  ratePerSqFt: number | null;
  /** Percent of gross revenue, the same for every tenant. */
  royaltyPercentage: number | null;
  /** Space Types the basic fee isn't charged on. */
  exemptSpaceTypes: string[];
  /** Individual units exempt regardless of their Space Type. */
  exemptUnitIds: string[];
  /** The committed Space Types list, for the exemption picker. */
  spaceTypes: string[];
  /** Every unit, named the way the rest of TOSP names them. */
  unitOptions: { id: string; label: string }[];
  /** All of it at once, on Commit — one statement about BMVA's charges. */
  onCommit: (fees: {
    ratePerSqFt: number | null;
    royaltyPercentage: number | null;
    exemptSpaceTypes: string[];
    exemptUnitIds: string[];
  }) => void;
  /** Only to show what the rate adds up to across the portfolio. */
  units: Unit[];
  tenants: Tenant[];
}

/** What's in a box, as a number — blank means "not set". */
const parsed = (text: string): number | null =>
  text.trim() ? parseNumeric(text) : null;

/** How a committed figure is shown when the box isn't being typed into. */
const asText = (value: number | null): string => (value === null ? "" : String(value));

// BMVA Fees.
//
// The village association sets both of these itself, one figure for everybody,
// and revises them for everybody at once — the rate went from $1.00 to $1.08
// this year. They used to be typed on each unit and each lease, which made a
// revision sixty edits with no way to tell whether one had been missed. They're
// entered here once instead, and the Unit and Occupancy forms read them.
//
// Edits are held here and only reach the rest of TOSP on Commit, like the
// lookup lists beside it. These two figures reprice every unit and every lease
// the moment they land, so a half-typed rate must not be one of them.
export default function BmvaFeesPanel({
  ratePerSqFt,
  royaltyPercentage,
  exemptSpaceTypes,
  exemptUnitIds,
  spaceTypes,
  unitOptions,
  onCommit,
  units,
  tenants,
}: BmvaFeesPanelProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  // Each figure's read-out is asked for, not shown by default.
  const [rateInfo, setRateInfo] = useState(false);
  const [royaltyInfo, setRoyaltyInfo] = useState(false);
  const [exemptInfo, setExemptInfo] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [exemptOpen, setExemptOpen] = useState(false);
  const [exemptUnitsOpen, setExemptUnitsOpen] = useState(false);
  // Text, not numbers: "1." mid-keystroke has to survive until it's committed.
  const [rateText, setRateText] = useState(asText(ratePerSqFt));
  const [royaltyText, setRoyaltyText] = useState(asText(royaltyPercentage));
  const [exempt, setExempt] = useState<string[]>(exemptSpaceTypes);
  const [exemptIds, setExemptIds] = useState<string[]>(exemptUnitIds);

  // Reset whenever the committed figures change underneath — including
  // straight after this panel's own commit.
  useEffect(() => {
    setRateText(asText(ratePerSqFt));
    setRoyaltyText(asText(royaltyPercentage));
    setExempt(exemptSpaceTypes);
    setExemptIds(exemptUnitIds);
  }, [ratePerSqFt, royaltyPercentage, exemptSpaceTypes, exemptUnitIds]);

  const draftRate = parsed(rateText);
  const draftRoyalty = parsed(royaltyText);
  const draftConfig = {
    bmvaRatePerSqFt: draftRate,
    bmvaExemptSpaceTypes: exempt,
    bmvaExemptUnitIds: exemptIds,
  };

  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  // What the rate WOULD be worth across every unit — the staged figures, not
  // the committed ones, since checking a change before it lands is the point.
  const annualTotal = units.reduce(
    (sum, unit) => sum + bmvaAnnualFee(unit, draftConfig),
    0,
  );

  /**
   * The Space Type exempting a unit, or null when nothing above covers it.
   *
   * Named rather than just flagged so the list can say WHY a row is ticked —
   * "via Storage" is the difference between a locked checkbox that makes sense
   * and one that looks broken.
   */
  const byType = (unitId: string): string | null => {
    const unit = units.find((candidate) => candidate.id === unitId);
    if (!unit) return null;
    return (
      exempt.find(
        (type) => type.trim().toLowerCase() === (unit.spaceType || "").trim().toLowerCase(),
      ) ?? null
    );
  };

  const exemptFor = (unitId: string) => byType(unitId) !== null || exemptIds.includes(unitId);

  /** Units the staged exemptions take out of the fee altogether. */
  const exemptUnits = units.filter((unit) => isBmvaExempt(draftConfig, unit)).length;

  const royaltyTenants = tenants.filter((tenant) =>
    (tenant.occupancies || []).some((occupancy) => occupancy.bmvaCap === null),
  ).length;

  const sameSet = (a: string[], b: string[]) =>
    a.length === b.length && a.every((item) => b.includes(item));

  const isDirty =
    rateText.trim() !== asText(ratePerSqFt).trim() ||
    royaltyText.trim() !== asText(royaltyPercentage).trim() ||
    !sameSet(exempt, exemptSpaceTypes) ||
    !sameSet(exemptIds, exemptUnitIds);

  // A box holding something that isn't a number would commit as "not set",
  // quietly dropping the rate back to each unit's own — so it's caught here.
  const invalid =
    (rateText.trim() !== "" && draftRate === null) ||
    (royaltyText.trim() !== "" && draftRoyalty === null);

  const commit = () => {
    onCommit({
      ratePerSqFt: draftRate,
      royaltyPercentage: draftRoyalty,
      exemptSpaceTypes: exempt,
      exemptUnitIds: exemptIds,
    });
    setCommitOpen(false);
    setExemptOpen(false);
    setExemptUnitsOpen(false);
  };

  return (
    <section className="tosp-config-panel">
      <div className="tosp-config-header">
        <h3 className="tosp-config-title">BMVA Fees</h3>
        <button
          type="button"
          className="tosp-help-toggle"
          aria-expanded={helpOpen}
          title="What's this?"
          onClick={() => setHelpOpen((open) => !open)}
        >
          ?
        </button>
      </div>

      {helpOpen && (
        <p className="tosp-config-note">
          Manage BMVA Royalty and Base Fees for all properties.
        </p>
      )}

      {/* One row each, the two boxes stacked and the same size — they're the
          same kind of statement, so they read as a pair rather than as two
          unrelated fields. What each figure works out to is behind its own
          "i": it's a check on a change, not something to read every visit. */}
      <div className="tosp-bmva-rows">
        <div className="tosp-bmva-row">
          <label className="tosp-label" htmlFor="tosp-bmva-rate">
            Base Fee PSF
            <button
              type="button"
              className="tosp-info-btn"
              aria-expanded={rateInfo}
              title={rateInfo ? "Hide" : "What this rate adds up to"}
              onClick={() => setRateInfo((open) => !open)}
            >
              i
            </button>
          </label>
          <div className="tosp-affix-wrap">
            <span className="tosp-affix tosp-affix-prefix">$</span>
            <input
              id="tosp-bmva-rate"
              className="manage-input tosp-has-prefix tosp-bmva-input"
              autoComplete="off"
              inputMode="decimal"
              value={rateText}
              onChange={(e) => setRateText(e.target.value)}
            />
          </div>
          {rateInfo && (
            <p className="tosp-field-hint tosp-bmva-info">
              {draftRate === null
                ? "Not set — units are still charging whatever rate they were last saved with."
                : `${formatCurrency(annualTotal)} a year across ${units.length} unit${
                    units.length === 1 ? "" : "s"
                  }${isDirty ? ", once committed" : ""}.`}
            </p>
          )}
        </div>

        <div className="tosp-bmva-row">
          <label className="tosp-label" htmlFor="tosp-bmva-royalty">
            Royalty Fee
            <button
              type="button"
              className="tosp-info-btn"
              aria-expanded={royaltyInfo}
              title={royaltyInfo ? "Hide" : "Who this royalty applies to"}
              onClick={() => setRoyaltyInfo((open) => !open)}
            >
              i
            </button>
          </label>
          <div className="tosp-affix-wrap">
            <input
              id="tosp-bmva-royalty"
              className="manage-input tosp-has-suffix tosp-bmva-input"
              autoComplete="off"
              inputMode="decimal"
              value={royaltyText}
              onChange={(e) => setRoyaltyText(e.target.value)}
            />
            <span className="tosp-affix tosp-affix-suffix">%</span>
          </div>
          {royaltyInfo && (
            <p className="tosp-field-hint tosp-bmva-info">
              {draftRoyalty === null
                ? "Not set — leases are still showing the rate each was reviewed at."
                : `Of gross revenue, on ${royaltyTenants} tenant${
                    royaltyTenants === 1 ? "" : "s"
                  } without a cap.`}
            </p>
          )}
        </div>

        {/* Exemptions are stated by Space Type rather than unit by unit, so a
            storage unit created next year is exempt the day it exists. */}
        <div className="tosp-bmva-row">
          <label className="tosp-label" htmlFor="tosp-bmva-exempt">
            {/* Reads as a count of what's actually exempt, so the label agrees
                with the box beside it. */}
            Base Fee Exempt {exempt.length === 1 ? "Space" : "Spaces"}
          </label>
          <button
            id="tosp-bmva-exempt"
            type="button"
            className="manage-input tosp-bmva-input tosp-bmva-picker"
            aria-expanded={exemptOpen}
            disabled={spaceTypes.length === 0}
            onClick={() => setExemptOpen((open) => !open)}
          >
            <span>
              {exempt.length === 0
                ? "None"
                : exempt.length === 1
                  ? exempt[0]
                  : `${exempt.length} types`}
            </span>
            <span aria-hidden="true">{exemptOpen ? "▾" : "▸"}</span>
          </button>

          {exemptOpen && (
            <div className="tosp-bmva-picker-list" role="group" aria-label="Base Fee Exempt">
              {spaceTypes.map((spaceType) => (
                <label key={spaceType} className="tosp-rolls-toggle">
                  <input
                    type="checkbox"
                    checked={exempt.includes(spaceType)}
                    onChange={() => setExempt((prev) => toggle(prev, spaceType))}
                  />
                  {spaceType}
                </label>
              ))}
              {/* An exemption committed before its Space Type was renamed or
                  removed still applies to nothing — worth seeing rather than
                  silently carrying. */}
              {exempt
                .filter((type) => !spaceTypes.includes(type))
                .map((type) => (
                  <label key={type} className="tosp-rolls-toggle tosp-bmva-picker-stale">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => setExempt((prev) => toggle(prev, type))}
                    />
                    {type} — no longer a Space Type
                  </label>
                ))}
            </div>
          )}

        </div>

        {/* Every exempt unit, however it got there — the types above are shown
            ticked here rather than left to be inferred, so this list is the
            whole answer to "who doesn't pay". The exceptions a type rule can't
            express are ticked here directly. */}
        <div className="tosp-bmva-row">
          <label className="tosp-label" htmlFor="tosp-bmva-exempt-units">
            Base Fee Exempt {exemptUnits === 1 ? "Unit" : "Units"}
            <button
              type="button"
              className="tosp-info-btn"
              aria-expanded={exemptInfo}
              title={exemptInfo ? "Hide" : "What these exemptions take out"}
              onClick={() => setExemptInfo((open) => !open)}
            >
              i
            </button>
          </label>
          <button
            id="tosp-bmva-exempt-units"
            type="button"
            className="manage-input tosp-bmva-input tosp-bmva-picker"
            aria-expanded={exemptUnitsOpen}
            disabled={unitOptions.length === 0}
            onClick={() => setExemptUnitsOpen((open) => !open)}
          >
            <span>
              {exemptUnits === 0
                ? "None"
                : exemptUnits === 1
                  ? unitOptions.find((option) => exemptFor(option.id))?.label || "1 unit"
                  : `${exemptUnits} units`}
            </span>
            <span aria-hidden="true">{exemptUnitsOpen ? "▾" : "▸"}</span>
          </button>

          {exemptUnitsOpen && (
            <div
              className="tosp-bmva-picker-list tosp-bmva-picker-scroll"
              role="group"
              aria-label="Base Fee Exempt Units"
            >
              {unitOptions.map((option) => {
                // Locked when the Space Type above already exempts it: there's
                // nothing to untick here, and the honest way to change it is
                // to change the rule that made it true.
                const viaType = byType(option.id);
                return (
                  <label
                    key={option.id}
                    className="tosp-rolls-toggle"
                    title={viaType ? `Exempt as ${viaType} — set in the row above` : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={viaType !== null || exemptIds.includes(option.id)}
                      // Not disabled — a locked-looking tick reads as broken.
                      // Clicking one that the Space Type above already covers
                      // does nothing, because there's nothing here to undo.
                      readOnly={viaType !== null}
                      onChange={() => {
                        if (viaType !== null) return;
                        setExemptIds((prev) => toggle(prev, option.id));
                      }}
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          )}

          {exemptInfo && (
            <p className="tosp-field-hint tosp-bmva-info">
              {exemptUnits === 0
                ? "Every unit is charged the base fee."
                : `${exemptUnits} unit${exemptUnits === 1 ? "" : "s"} ${
                    exemptUnits === 1 ? "is" : "are"
                  } exempt from paying base fee.`}
            </p>
          )}
        </div>
      </div>

      {invalid && (
        <p className="tosp-field-error">Both figures must be numbers, or left blank.</p>
      )}

      <div className="tosp-config-footer">
        <button
          type="button"
          className="tosp-commit-btn"
          disabled={!isDirty || invalid}
          title={isDirty ? undefined : "No changes to commit"}
          onClick={() => setCommitOpen(true)}
        >
          Commit
        </button>
      </div>

      <DeleteConfirmModal
        isOpen={commitOpen}
        title="Commit BMVA Fees"
        message={`This reprices every unit and every lease at once — ${
          draftRate === null ? "no rate" : `${formatCurrency(draftRate)} per square foot`
        } and ${
          draftRoyalty === null ? "no royalty" : `${draftRoyalty}% of gross revenue`
        }.${
          exemptUnits
            ? ` ${exemptUnits} unit${exemptUnits === 1 ? "" : "s"} pay no basic fee${
                exempt.length ? ` (${exempt.join(", ")}` : ""
              }${exempt.length && exemptIds.length ? ", plus named units" : ""}${
                exempt.length ? ")" : ""
              }.`
            : ""
        } Existing BMVA Caps are unaffected.`}
        confirmLabel="Continue"
        confirmColor="#1faa59"
        onClose={() => setCommitOpen(false)}
        onConfirm={commit}
      />
    </section>
  );
}
